import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Container, Card, Spinner, Alert, Button, ListGroup } from 'react-bootstrap';
import { client } from '../../lib/AmplifyClient';
import type { Schema } from '../../../amplify/data/resource';
import { useUserContext } from '../Context/UserContext';

type Issue = Schema['Issue']['type'];

/** Allowed conflict types */
const ALLOWED_CONFLICT_TYPES = new Set(['personality', 'values', 'task'] as const);
type AllowedConflict = 'personality' | 'values' | 'task';

/** Candidate (UI-level) */
type Candidate = { statement: string; conflictType?: AllowedConflict; score?: number };

/** Routes and statuses */
const communicationPath = (sid: string) => `/session/${encodeURIComponent(sid)}/communication`;
const solutionsPath = (sid: string) => `/session/${encodeURIComponent(sid)}/solutions`;
const STATUS_RESOLVING = 'resolving_issue';
const STATUS_AGREED = 'agreed_on_issue';
const FIELD_NO_COMMON_CONFLICT = 'noCommonConflict';

/* ---------------- Error Boundary ---------------- */
class AgreeErrorBoundary extends React.Component<{ children: React.ReactNode }, { hasError: boolean; msg: string }> {
  constructor(props: any) { super(props); this.state = { hasError: false, msg: '', }; }
  static getDerivedStateFromError(err: any) { return { hasError: true, msg: String(err?.message ?? err) }; }
  componentDidCatch(error: any, info: any) {
    // eslint-disable-next-line no-console
    console.error('[AgreeIssue][Boundary] Caught render error:', error, info?.componentStack);
  }
  render() {
    if (this.state.hasError) {
      return (
        <Container className="mt-5">
          <Alert variant="warning">A transient view error was intercepted. Details: {this.state.msg}</Alert>
        </Container>
      );
    }
    return this.props.children as any;
  }
}

/* ---------------- Null-safety utilities ---------------- */
const norm = <T,>(xs: (T | null | undefined)[] | null | undefined): T[] =>
  Array.isArray(xs) ? xs.filter((x): x is T => x != null) : [];

function unwrap<T>(maybeWrapped: unknown): T | null {
  if (maybeWrapped && typeof maybeWrapped === 'object' && 'data' in (maybeWrapped as any)) {
    const { data } = maybeWrapped as { data: T | null };
    return data ?? null;
  }
  return (maybeWrapped as T) ?? null;
}

function coerceStrings(arr?: (string | null)[] | null): string[] {
  return (arr ?? []).filter((s): s is string => typeof s === 'string' && s.trim().length > 0);
}

function parseAwsJson<T = any>(val: unknown): T | null {
  if (val == null) return null;
  if (typeof val === 'string') { try { return JSON.parse(val) as T; } catch { return null; } }
  if (typeof val === 'object') return val as T;
  return null;
}

function normalizeConflictType(x?: string | null): AllowedConflict | undefined {
  const ct = (x ?? '').trim().toLowerCase();
  return ALLOWED_CONFLICT_TYPES.has(ct as any) ? (ct as AllowedConflict) : undefined;
}

function isInitiator(me: string | null | undefined, issue: Issue | null): boolean {
  return !!me && !!issue && me.trim().toLowerCase() === (issue.initiatorEmail ?? '').trim().toLowerCase();
}
function isCollaborator(me: string | null | undefined, issue: Issue | null): boolean {
  return !!me && !!issue && me.trim().toLowerCase() === (issue.collaboratorEmail ?? '').trim().toLowerCase();
}

/** Normalize Issue arrays to remove null elements */
function normalizeIssue(row: any): Issue {
  if (!row || typeof row !== 'object') return row as Issue;
  return {
    ...row,
    initiatorIssue: norm<string>(row?.initiatorIssue),
    collaboratorIssue: norm<string>(row?.collaboratorIssue),
    desiredOutcomeInitiator: norm<string>(row?.desiredOutcomeInitiator),
    desiredOutcomeCollaborator: norm<string>(row?.desiredOutcomeCollaborator),
    whyOutcomeMattersInitiator: norm<string>(row?.whyOutcomeMattersInitiator ?? row?.whyOutcomeInitiator),
    whyOutcomeMattersCollaborator: norm<string>(row?.whyOutcomeMattersCollaborator ?? row?.whyOutcomeCollaborator),
    // keep only true arrays here; JSON fields are handled by readCandidates()
    proposedIssueCandidates: norm<string>(row?.proposedIssueCandidates),
    reframedIssueCandidates: norm<string>(row?.reframedIssueCandidates),
    initiatorAcceptedIssues: norm<string>(row?.initiatorAcceptedIssues),
    collaboratorAcceptedIssues: norm<string>(row?.collaboratorAcceptedIssues),
    initiatorReviewedIssues: norm<string>(row?.initiatorReviewedIssues),
    collaboratorReviewedIssues: norm<string>(row?.collaboratorReviewedIssues),
  } as Issue;
}

/** Build candidates robustly */
function readCandidates(issue: Issue | null): Candidate[] {
  if (!issue) return [];

  const rawJson =
    (issue as any).proposedIssueCandidatesJson ?? null;

  const pj = parseAwsJson<{ candidates?: Array<{ statement?: unknown; conflictType?: unknown; score?: unknown }> }>(rawJson);

  let list: Candidate[] = Array.isArray(pj?.candidates)
    ? pj!.candidates.map((c): Candidate => ({
        statement: String((c as any)?.statement ?? '').trim(),
        conflictType: normalizeConflictType(
          typeof (c as any)?.conflictType === 'string' ? (c as any).conflictType : String((c as any)?.conflictType ?? '')
        ),
        score: typeof (c as any)?.score === 'number' ? ((c as any).score as number) : undefined,
      })).filter(c => c.statement.length > 0)
    : [];

  if (list.length === 0) {
    const pi = parseAwsJson<any>((issue as any).proposedIssues);
    const arr1: string[] = Array.isArray(pi) ? pi : Array.isArray(pi?.issues) ? pi.issues : [];
    if (arr1.length) list = arr1.map(s => ({ statement: String(s).trim() }));
  }
  if (list.length === 0) {
    const flatA: string[] = coerceStrings((issue as any).proposedIssueCandidates);
    if (flatA.length) list = flatA.map(s => ({ statement: s.trim() }));
  }
  if (list.length === 0) {
    const flatB: string[] = coerceStrings((issue as any).reframedIssueCandidates);
    if (flatB.length) list = flatB.map(s => ({ statement: s.trim() }));
  }

  list.sort((a, b) => {
    const as = typeof a.score === 'number' ? a.score : -Infinity;
    const bs = typeof b.score === 'number' ? b.score : -Infinity;
    return bs - as;
  });

  return list.slice(0, 4);
}

/** Inputs completeness */
function seemsReadyToGenerate(it: Issue | null) {
  if (!it) return false;
  const has = (a?: (string | null)[] | null) => Array.isArray(a) && a.filter(Boolean).length > 0;
  const whyI = (it as any).whyOutcomeMattersInitiator ?? (it as any).whyOutcomeInitiator;
  const whyC = (it as any).whyOutcomeMattersCollaborator ?? (it as any).whyOutcomeCollaborator;

  return (
    has((it as any).initiatorIssue) &&
    has((it as any).desiredOutcomeInitiator) &&
    has(whyI) &&
    has((it as any).collaboratorIssue) &&
    has((it as any).desiredOutcomeCollaborator) &&
    has(whyC)
  );
}

/** Case-insensitive compare */
function eq(a: string, b: string) { return a.trim().toLowerCase() === b.trim().toLowerCase(); }

export default function AgreeIssueComponent() {
  const { id: routeId } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const { email: userEmail } = useUserContext();

  const sessionId = useMemo(() => decodeURIComponent(routeId ?? '').trim().replace(/\/+$/, ''), [routeId]);

  const [issue, setIssue] = useState<Issue | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [submittedOnce, setSubmittedOnce] = useState(false); // hide button and options after first click

  // Polling control
  const pollTimerRef = useRef<number | null>(null);
  const stopPolling = () => { if (pollTimerRef.current) { window.clearInterval(pollTimerRef.current); pollTimerRef.current = null; } };

  // Global traps
  useEffect(() => {
    const onErr = (e: ErrorEvent) => console.error('[GlobalError]', e?.error || e);
    const onRej = (e: PromiseRejectionEvent) => console.error('[UnhandledRejection]', e?.reason);
    window.addEventListener('error', onErr);
    window.addEventListener('unhandledrejection', onRej);
    return () => { window.removeEventListener('error', onErr); window.removeEventListener('unhandledrejection', onRej); };
  }, []);

  const meIsInitiator = useMemo(() => !!issue && isInitiator(userEmail, issue), [userEmail, issue]);
  const meIsCollaborator = useMemo(() => !!issue && isCollaborator(userEmail, issue), [userEmail, issue]);

  const candidates = useMemo<Candidate[]>(() => {
    const result = readCandidates(issue);
    console.log('[AgreeIssue] Candidates computed:', result.length, 'from issue:', issue?.id ?? '(none)');
    console.log('[AgreeIssue] Issue fields:', {
      proposedIssueCandidatesJson: (issue as any)?.proposedIssueCandidatesJson,
      proposedIssueCandidatesJs: (issue as any)?.proposedIssueCandidatesJs,
      proposedIssues: (issue as any)?.proposedIssues,
      proposedIssueCandidates: (issue as any)?.proposedIssueCandidates,
      reframedIssueCandidates: (issue as any)?.reframedIssueCandidates,
    });
    return result;
  }, [issue]);

  const safeCandidates: Candidate[] = Array.isArray(candidates)
    ? candidates.filter((c) => !!c && typeof c.statement === 'string' && c.statement.trim() !== '')
    : [];

  const candKey = safeCandidates.map((c) => String(c?.statement ?? '')).join('||');

  const initiatorAccepted = useMemo(() => coerceStrings((issue as any)?.initiatorAcceptedIssues), [issue]);
  const collaboratorAccepted = useMemo(() => coerceStrings((issue as any)?.collaboratorAcceptedIssues), [issue]);

  const initiatorAcceptedKey = initiatorAccepted.join('||');
  const collaboratorAcceptedKey = collaboratorAccepted.join('||');

  const initialMineAccepted = useMemo<string[]>(() => {
    if (meIsInitiator) return initiatorAccepted;
    if (meIsCollaborator) return collaboratorAccepted;
    return [];
  }, [meIsInitiator, meIsCollaborator, initiatorAcceptedKey, collaboratorAcceptedKey]);

  const [myAcceptedLocal, setMyAcceptedLocal] = useState<Set<string>>(new Set(initialMineAccepted));
  const [myDeclinedLocal, setMyDeclinedLocal] = useState<Set<string>>(new Set());
  useEffect(() => { setMyAcceptedLocal(new Set(initialMineAccepted)); }, [initialMineAccepted.join('||')]);

  // One-time full read for ground truth
  useEffect(() => {
    if (!sessionId) return;
    (async () => {
      try {
        const raw = await client.models.Issue.get({ id: sessionId }, { authMode: 'userPool' }) as unknown;
        console.log('[AgreeIssue] debug get() full row:', JSON.stringify(unwrap<Issue>(raw), null, 2));
      } catch (e) {
        console.error('[AgreeIssue] debug get() error:', e);
      }
    })();
  }, [sessionId]);

  // Prefetch once to paint fast if available
  useEffect(() => {
    if (!sessionId) return;
    (async () => {
      try {
        const raw = await client.models.Issue.get({ id: sessionId }, { authMode: 'userPool' }) as unknown;
        const data = unwrap<Issue>(raw);
        if (data && typeof (data as any).id === 'string') {
          setIssue(normalizeIssue(data));
          // do not force loading=false here; let observeQuery or polling decide when data is truly ready
        }
      } catch (e) {
        console.error('[AgreeIssue] prefetch error:', e);
        setError('Failed to load session');
        setLoading(false);
      }
    })();
  }, [sessionId]);

  // observeQuery with stricter null filtering and safe hydration
  useEffect(() => {
    if (!sessionId || !userEmail) return;

    let unsubscribed = false;
    let firstEmission = true;

    console.log('[AgreeIssue][observeQuery] start for', sessionId);

    const sub = client.models.Issue.observeQuery({
      filter: { id: { eq: sessionId } },
      authMode: 'userPool',
    }).subscribe({
      next: async ({ items, isSynced }: { items: any[]; isSynced?: boolean }) => {
        if (unsubscribed) return;

        const arr = Array.isArray(items)
          ? items.filter(
              (x) => x && typeof x === 'object' && typeof (x as any).id === 'string'
            )
          : [];
        if (arr.length === 0) {
          console.log('[AgreeIssue][observeQuery] skip emission, null or invalid items');
          return;
        }

        const row = arr[0];
        console.log('[AgreeIssue][observeQuery] valid emission', {
          count: arr.length,
          id: row.id,
          isSynced,
          firstEmission,
        });

        try {
          const fullRaw = await client.models.Issue.get({ id: row.id }, { authMode: 'userPool' }) as unknown;
          const full = unwrap<Issue>(fullRaw);
          if (!unsubscribed) setIssue(full ? normalizeIssue(full) : normalizeIssue(row));
        } catch (e) {
          console.warn('[AgreeIssue][observeQuery] hydration fallback', e);
          if (!unsubscribed) setIssue(normalizeIssue(row));
        }

        if (isSynced || readCandidates(row).length > 0) {
          if (!unsubscribed) setLoading(false);
        }

        firstEmission = false;
      },
      error: (err) => {
        console.error('[AgreeIssue][observeQuery] error', err);
        if (!unsubscribed) {
          setError('Failed to load invitation.');
          setLoading(false);
        }
      },
    });

    return () => {
      unsubscribed = true;
      try { sub.unsubscribe(); } catch {}
      console.log('[AgreeIssue][observeQuery] unsubscribed');
    };
  }, [sessionId, userEmail]);

  // Polling fallback: fetch every 3s until candidates appear or status advances
  useEffect(() => {
    if (!sessionId) return;

    const shouldPoll =
      !loading &&
      (!issue || readCandidates(issue).length === 0) &&
      (!issue || (issue.status !== STATUS_AGREED && !(issue as any)?.[FIELD_NO_COMMON_CONFLICT]));

    if (!shouldPoll) { stopPolling(); return; }

    if (pollTimerRef.current) return;

    console.log('[AgreeIssue][poll] starting fallback poll every 3s');
    pollTimerRef.current = window.setInterval(async () => {
      try {
        const raw = await client.models.Issue.get({ id: sessionId }, { authMode: 'userPool' }) as unknown;
        const data = unwrap<Issue>(raw);
        const normalized = data ? normalizeIssue(data) : null;
        const have = readCandidates(normalized).length;

        console.log('[AgreeIssue][poll] fetched. hasCandidates=', have, 'status=', normalized?.status);

        if (normalized) setIssue(normalized);
        if (have > 0 || normalized?.status === STATUS_AGREED || (normalized as any)?.[FIELD_NO_COMMON_CONFLICT]) {
          stopPolling();
          setLoading(false);
        }
      } catch (e) {
        console.warn('[AgreeIssue][poll] error:', e);
      }
    }, 3000) as unknown as number;

    return stopPolling;
  }, [sessionId, loading, issue?.status, (issue as any)?.[FIELD_NO_COMMON_CONFLICT], candKey]);

  async function readIssueOnce() {
    const raw = (await client.models.Issue.get({ id: sessionId }, { authMode: 'userPool' })) as unknown;
    const data = unwrap<Issue>(raw);
    return data && typeof (data as any).id === 'string' ? normalizeIssue(data) : null;
  }

  function toggleYes(statement: string) {
    setMyAcceptedLocal((prev) => {
      const next = new Set(prev);
      if (Array.from(next).some((s) => eq(s, statement))) {
        Array.from(next).forEach((s) => { if (eq(s, statement)) next.delete(s); });
      } else { next.add(statement); }
      return next;
    });
    setMyDeclinedLocal((prev) => {
      const next = new Set(prev);
      Array.from(next).forEach((s) => { if (eq(s, statement)) next.delete(s); });
      return next;
    });
  }

  function toggleNo(statement: string) {
    setMyDeclinedLocal((prev) => {
      const next = new Set(prev);
      const isNo = Array.from(next).some((s) => eq(s, statement));
      if (isNo) {
        Array.from(next).forEach((s) => { if (eq(s, statement)) next.delete(s); });
      } else { next.add(statement); }
      return next;
    });
    setMyAcceptedLocal((prev) => {
      const next = new Set(prev);
      Array.from(next).forEach((s) => { if (eq(s, statement)) next.delete(s); });
      return next;
    });
  }

  const initiatorDone = useMemo(() => {
    const r = coerceStrings((issue as any)?.initiatorReviewedIssues).filter((s) => safeCandidates.some((c) => eq(c.statement, s)));
    return safeCandidates.length > 0 && r.length >= safeCandidates.length;
  }, [JSON.stringify((issue as any)?.initiatorReviewedIssues ?? []), candKey]);

  const collaboratorDone = useMemo(() => {
    const r = coerceStrings((issue as any)?.collaboratorReviewedIssues).filter((s) => safeCandidates.some((c) => eq(c.statement, s)));
    return safeCandidates.length > 0 && r.length >= safeCandidates.length;
  }, [JSON.stringify((issue as any)?.collaboratorReviewedIssues ?? []), candKey]);

  const myDone = meIsInitiator ? initiatorDone : meIsCollaborator ? collaboratorDone : false;

  const allIssuesReviewed = useMemo(() => {
    const reviewedSet = new Set<string>([
      ...Array.from(myAcceptedLocal),
      ...Array.from(myDeclinedLocal),
    ]);
    return safeCandidates.every((c) => 
      Array.from(reviewedSet).some((s) => eq(s, c.statement))
    );
  }, [safeCandidates, Array.from(myAcceptedLocal).join('||'), Array.from(myDeclinedLocal).join('||')]);

  async function submitSelections() {
    if (!issue || !userEmail) return;
    if (myDone) return;
    if (!allIssuesReviewed) return;

    if (!submittedOnce) setSubmittedOnce(true);

    setSaving(true);

    const allStatements = safeCandidates.map((c) => c.statement);
    const reviewedSet = new Set<string>([
      ...Array.from(myAcceptedLocal),
      ...Array.from(myDeclinedLocal),
    ]);
    const reviewed = allStatements.filter((s) => Array.from(reviewedSet).some((t) => eq(s, t)));

    const patch: any = { id: sessionId, lastActivityAt: new Date().toISOString() };

    if (meIsInitiator) {
      patch.initiatorAcceptedIssues = Array.from(myAcceptedLocal);
      patch.initiatorReviewedIssues = reviewed;
    } else if (meIsCollaborator) {
      patch.collaboratorAcceptedIssues = Array.from(myAcceptedLocal);
      patch.collaboratorReviewedIssues = reviewed;
    }

    await client.models.Issue.update(patch, { authMode: 'userPool' });

    const latest = await readIssueOnce();

    const acceptedNone = Array.from(myAcceptedLocal).length === 0;
    const reviewedAll = safeCandidates.length > 0 && reviewed.length >= safeCandidates.length;

    if (acceptedNone && reviewedAll) {
      try {
        await client.models.Issue.update(
          { id: sessionId, [FIELD_NO_COMMON_CONFLICT]: true as any, status: STATUS_RESOLVING, lastActivityAt: new Date().toISOString() } as any,
          { authMode: 'userPool' }
        );
      } catch {}

      setSaving(false);
      navigate(communicationPath(sessionId), { replace: true });
      return;
    }

    const mine = Array.from(myAcceptedLocal);
    const other = meIsInitiator
      ? coerceStrings((latest as any)?.collaboratorAcceptedIssues)
      : coerceStrings((latest as any)?.initiatorAcceptedIssues);

    const agreed: string[] = [];
    for (const s of mine) { if (other.some((o) => eq(o, s))) agreed.push(s); }

    if (agreed.length > 0) {
      const agreedFirst =
        safeCandidates.find(c => agreed.some(s => eq(s, c.statement)))?.statement
        ?? agreed[0];

      const agreedCand = safeCandidates.find(c => eq(c.statement, agreedFirst));
      const agreedType = normalizeConflictType(agreedCand?.conflictType);

      const existingMeta = parseAwsJson<any>((issue as any)?.agreedIssueMeta) || {};
      const nextMeta = { ...existingMeta, agreedIssue: agreedFirst, agreedConflictType: agreedType };

      try {
        await client.models.Issue.update(
          { id: sessionId, agreedIssue: agreedFirst, agreedConflictType: agreedType as any, agreedIssueMeta: JSON.stringify(nextMeta), status: STATUS_AGREED, lastActivityAt: new Date().toISOString() } as any,
          { authMode: 'userPool' }
        );
      } catch {}

      setSaving(false);
      navigate(solutionsPath(sessionId), { replace: true });
      return;
    }

    const initReviewed = coerceStrings((latest as any)?.initiatorReviewedIssues);
    const collReviewed = coerceStrings((latest as any)?.collaboratorReviewedIssues);
    const initiatorDoneNow = safeCandidates.length > 0 && initReviewed.length >= safeCandidates.length;
    const collaboratorDoneNow = safeCandidates.length > 0 && collReviewed.length >= safeCandidates.length;

    if (initiatorDoneNow && collaboratorDoneNow && agreed.length === 0) {
      try {
        await client.models.Issue.update(
          { id: sessionId, status: STATUS_RESOLVING, lastActivityAt: new Date().toISOString() } as any,
          { authMode: 'userPool' }
        );
      } catch {}
      setSaving(false);
      navigate(communicationPath(sessionId), { replace: true });
      return;
    }

    setSaving(false);
  }

  const agreedWithMeta = useMemo(() => {
    const mine = Array.from(myAcceptedLocal);
    const other = meIsInitiator ? collaboratorAccepted : meIsCollaborator ? initiatorAccepted : [];
    const both = safeCandidates.filter(
      (c) => mine.some((s) => eq(s, c.statement)) && other.some((o) => eq(o, c.statement))
    );
    both.sort((a, b) => {
      const as = typeof a.score === 'number' ? a.score : -Infinity;
      const bs = typeof b.score === 'number' ? b.score : -Infinity;
      return bs - as;
    });
    return both;
  }, [
    Array.from(myAcceptedLocal).join('||'),
    initiatorAccepted.join('||'),
    collaboratorAccepted.join('||'),
    candKey,
    meIsInitiator,
    meIsCollaborator,
  ]);

  useEffect(() => {
    if ((issue as any)?.[FIELD_NO_COMMON_CONFLICT]) {
      navigate(communicationPath(sessionId), { replace: true });
    }
  }, [(issue as any)?.[FIELD_NO_COMMON_CONFLICT], sessionId, navigate]);

  useEffect(() => {
    if (!issue) return;
    if (issue.status === STATUS_AGREED || (issue as any)?.agreedIssue) {
      navigate(solutionsPath(sessionId), { replace: true });
    }
  }, [issue?.status, (issue as any)?.agreedIssue, sessionId, navigate]);

  useEffect(() => {
    if (!issue) return;
    if (issue.status === STATUS_RESOLVING) {
      navigate(communicationPath(sessionId), { replace: true });
    }
  }, [issue?.status, sessionId, navigate]);

  // Loading state logic
  useEffect(() => {
    if (safeCandidates.length > 0 && loading) {
      console.log('[AgreeIssue] hiding spinner due to candidates presence');
      setLoading(false);
    }
  }, [safeCandidates.length, loading]);

  console.log('[AgreeIssue] Render - loading:', loading, 'userEmail:', userEmail, 'issue:', issue?.id ?? '(none)');
  console.log('[AgreeIssue] about to render candidates list. count=', safeCandidates.length, safeCandidates);

  if (loading) {
    return (
      <Container className="mt-5 text-center">
        <Spinner animation="border" role="status" />
        <div className="mt-3">Loading…</div>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="mt-5">
        <Alert variant="danger">{error}</Alert>
        <Button variant="outline-secondary" onClick={() => navigate(`/session/${sessionId}`)}>
          Back to Session
        </Button>
      </Container>
    );
  }

  if (!issue) {
    return (
      <Container className="mt-5">
        <Alert variant="warning">Loading session…</Alert>
      </Container>
    );
  }

  if (!userEmail) {
    return (
      <Container className="mt-5 text-center">
        <Spinner animation="border" role="status" />
        <div className="mt-3">Checking authentication…</div>
      </Container>
    );
  }

  const readyButNoProposals = seemsReadyToGenerate(issue) && safeCandidates.length === 0;
  const initiatorFirst = (issue.initiatorFirstName ?? '').trim() || 'the initiator';
  const collaboratorFirst = (issue.collaboratorFirstName ?? '').trim() || 'your collaborator';

  return (
    <AgreeErrorBoundary>
      <Container className="mt-4">
        <Card className="mx-auto" style={{ maxWidth: 900 }}>
          <Card.Header as="h5">Agree on the Issue</Card.Header>
          <Card.Body>
            {meIsCollaborator && collaboratorAccepted.length > 0 && !initiatorDone && (
              <Alert variant="info" className="mb-3">
                Thanks — we've saved your choice. Waiting for <strong>{initiatorFirst}</strong> to review and respond to the proposed issues.
              </Alert>
            )}

            {meIsCollaborator && collaboratorDone && !initiatorDone && collaboratorAccepted.length === 0 && (
              <Alert variant="info" className="mb-3">
                Waiting for <strong>{initiatorFirst}</strong> to review the issues…
              </Alert>
            )}

            {meIsInitiator && initiatorDone && !collaboratorDone && (
              <Alert variant="info" className="mb-3">
                Thanks — your responses are saved. Waiting for <strong>{collaboratorFirst}</strong> to review the proposed issues.
              </Alert>
            )}

            {initiatorDone && collaboratorDone && agreedWithMeta.length > 0 && (
              <Alert variant="success" className="mb-3">
                <div className="fw-semibold mb-1">Great news — you both agreed on an issue!</div>
                <ul className="mb-3">
                  {agreedWithMeta.map((a, i) => (
                    <li key={`agreed-${i}`}>{a.statement}</li>
                  ))}
                </ul>

                <div className="d-flex flex-wrap gap-2">
                  <Button variant="primary" onClick={() => navigate(solutionsPath(sessionId))}>Continue</Button>
                </div>
              </Alert>
            )}

            {safeCandidates.length === 0 && (
              <>
                {readyButNoProposals ? (
                  <div className="d-flex align-items-center gap-2">
                    <Spinner animation="border" role="status" size="sm" />
                    <span>After both parties answer the three questions proposed issues will be determined</span>
                  </div>
                ) : (
                  <Alert variant="secondary" className="mb-3">
                    No proposed issues found yet. We'll continue with the perspective pathway if inputs are incomplete.
                  </Alert>
                )}
              </>
            )}

            {safeCandidates.length > 0 && (
              <>
                {!submittedOnce ? (
                  <>
                    <div className="mb-2 text-muted">Here are four proposed issues for your review</div>
                    <ListGroup>
                      {safeCandidates.map((c, i) => {
                        const iSelected = Array.from(myAcceptedLocal).some((s) => eq(s, c.statement));
                        const iDeclined = Array.from(myDeclinedLocal).some((s) => eq(s, c.statement));
                        const disableChoice = myDone || saving;

                        return (
                          <ListGroup.Item key={`cand-${i}`} className="pt-3 pb-3">
                            <div className="d-flex justify-content-between align-items-start">
                              <div>
                                <div className="fw-semibold">Option {i + 1}</div>
                                <div className="fs-6">{c.statement}</div>
                              </div>
                              {typeof c.score === 'number' && (
                                <div className="text-end">
                                  <div className="small text-muted mt-1">Score: {c.score.toFixed(2)}</div>
                                </div>
                              )}
                            </div>

                            <Card className="mt-3" bg="light" text="dark">
                              <Card.Body className="py-2">
                                <div className="d-flex flex-wrap align-items-center justify-content-between">
                                  <div className="mb-2 mb-sm-0">
                                    <span>Do you agree this frames the issue accurately?</span>
                                  </div>
                                  <div className="d-flex gap-2">
                                    <Button
                                      size="sm"
                                      variant={iSelected ? 'success' : 'outline-success'}
                                      onClick={() => toggleYes(c.statement)}
                                      disabled={disableChoice}
                                    >
                                      {iSelected ? 'You agreed' : 'Yes, I agree'}
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant={iDeclined ? 'danger' : 'outline-danger'}
                                      onClick={() => toggleNo(c.statement)}
                                      disabled={disableChoice}
                                    >
                                      {iDeclined ? 'You chose No' : 'No'}
                                    </Button>
                                  </div>
                                </div>
                              </Card.Body>
                            </Card>
                          </ListGroup.Item>
                        );
                      })}
                    </ListGroup>

                    {/* Hide the submit button after first click */}
                    {!submittedOnce && (
                      <>
                        {!allIssuesReviewed && (
                          <Alert variant="warning" className="mt-3">
                            Please review all {safeCandidates.length} issues by selecting Yes or No for each one before submitting.
                          </Alert>
                        )}
                        <div className="d-flex justify-content-end mt-3">
                          <Button variant="primary" onClick={submitSelections} disabled={saving || myDone || !allIssuesReviewed}>
                            {saving ? (<><Spinner animation="border" size="sm" className="me-2" /> Saving…</>) : ('Submit Selections')}
                          </Button>
                        </div>
                      </>
                    )}
                  </>
                ) : (
                  <Alert variant="info" className="mt-3">
                    After both parties review the issues, the proposed solutions will be presented to both of you.
                  </Alert>
                )}
              </>
            )}
          </Card.Body>
        </Card>
      </Container>
    </AgreeErrorBoundary>
  );
}